import { ReactNode, useEffect } from 'react';
import {
  AllAccessLocationFilterSelector,
  defaultTimePeriods,
  useAnalyticsOrgLocations,
  useTimePeriodPopoverSelector,
} from '@frontend/analytics-shared';
import { Icon } from '@frontend/icons';
import { humanizeDateDifference } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { defaultTimePeriod } from '../../constants';
import { usePhoneAnalyticsShallowStore } from '../../hooks/use-phone-analytics-store';
import { trackingIds } from '../../tracking-ids';
import { PhoneAnalyticsFiltersTray } from './phone-analytics-filters-tray';

export const PhoneAnalyticsFilters = ({ activeTab, children }: { activeTab: string; children?: ReactNode }) => {
  const { filters, isDemoAccount, setStore } = usePhoneAnalyticsShallowStore('filters', 'isDemoAccount', 'setStore');

  const { hasAccessToMultipleLocations, locationIds, locationNames, preselectedAccessibleLocationIds } =
    useAnalyticsOrgLocations({
      isAllAccessScopeEnabled: true,
      isDemoAccount,
      module: 'PHONE',
    });

  const { selectedPeriod, TimePeriodPopoverSelector, timePeriods } = useTimePeriodPopoverSelector({
    defaultPeriod: defaultTimePeriod,
    timePeriods: defaultTimePeriods,
    values: {
      endDate: filters.end_date,
      startDate: filters.start_date,
    },
  });

  useEffect(() => {
    if (filters?.start_date && filters.end_date) {
      setStore({
        filterHintText: selectedPeriod
          ? timePeriods?.[selectedPeriod]?.label
          : humanizeDateDifference(filters?.start_date, filters?.end_date),
      });
    }
  }, [filters, selectedPeriod, timePeriods]);

  return (
    <div css={{ display: 'flex', gap: theme.spacing(2), paddingBottom: theme.spacing(2) }}>
      {hasAccessToMultipleLocations && (
        <AllAccessLocationFilterSelector
          locations={locationNames}
          onChange={(locationIds) => {
            setStore({
              filters: {
                location_id: locationIds,
              },
            });
          }}
          value={filters.location_id}
          defaultValue={preselectedAccessibleLocationIds}
          trackingId={trackingIds.locationsFilter}
          renderIcon={(isActive) => <Icon name={'location-small'} color={isActive ? 'white' : 'default'} />}
        />
      )}

      <TimePeriodPopoverSelector
        customTimePeriodTracking={{
          filteredLocations: JSON.stringify(filters.location_id ?? []),
          filteredLocationsCount: filters.location_id?.length ?? 0,
          selectedLocationIds: JSON.stringify(locationIds),
          totalLocationsCount: preselectedAccessibleLocationIds?.length ?? 0,
        }}
        onChange={({ startDate, endDate }) => {
          setStore({
            filters: {
              end_date: endDate,
              start_date: startDate,
            },
          });
        }}
        persistDefaultLabel
        trackingIdBase={trackingIds.timeFilter}
      />
      <div css={{ display: 'flex', flex: 1, justifyContent: 'flex-end', alignItems: 'center', gap: theme.spacing(2) }}>
        {children}
        <PhoneAnalyticsFiltersTray isTableView={activeTab === 'table-view'} />
      </div>
    </div>
  );
};
